import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import {
  Plus, Lock, Unlock, Check, ChevronRight, ChevronLeft,
  Trash2, Edit3, Calendar, Tag, Link2, Zap, X, Archive,
  MoreHorizontal, Flag, ArrowRight, Circle, CheckCircle2,
  TrendingUp, TrendingDown, Minus as MinusIcon, Flame,
  FileText, ExternalLink, AlertCircle, GripVertical,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const COLORS = [
  "#5A90F5","#FF8A00","#A855F7","#00C781","#EF4444",
  "#14B8A6","#F59E0B","#EC4899","#6366F1","#84CC16",
];

const EMOJIS = ["🎯","🚀","💡","📐","🔥","⚡","🌱","🏆","💎","🗺️","🧩","📦"];

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((d - now) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff < 7) return `${diff}d left`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function isOverdue(due_date) {
  if (!due_date) return false;
  return new Date(due_date) < new Date() && new Date(due_date).toDateString() !== new Date().toDateString();
}

// ─── MomentumBadge ────────────────────────────────────────────────────────────
function MomentumBadge({ momentum }) {
  if (momentum === null || momentum === undefined) return null;
  const Icon = momentum > 0.5 ? TrendingUp : momentum > 0 ? MinusIcon : TrendingDown;
  const color = momentum > 0.5 ? "#00C781" : momentum > 0 ? "#F59E0B" : "#52525B";
  return (
    <span className="inline-flex items-center gap-1 text-[10px]" style={{ color }}>
      <Icon className="w-3 h-3" />
      {momentum > 0 ? `${momentum}/day` : "Stalled"}
    </span>
  );
}

// ─── ProofModal ───────────────────────────────────────────────────────────────
function ProofModal({ step, onClose, onSubmit }) {
  const [type, setType] = useState("text");
  const [value, setValue] = useState("");

  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[15px] font-semibold text-[#EDEDED]">Attach proof</h3>
            <p className="text-[11px] text-[#52525B] mt-0.5">What was delivered for "{step.title}"?</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#52525B]"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          {[["text","Text"],["link","Link"],["file","File ref"]].map(([t, label]) => (
            <button key={t} onClick={() => setType(t)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors ${type === t ? "border-[#5A90F5] bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA]"}`}>
              {label}
            </button>
          ))}
        </div>
        <textarea
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={type === "text" ? "Describe what was completed..." : type === "link" ? "https://..." : "Path or filename..."}
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none resize-none focus:border-[#5A90F5] transition-colors"
          rows={4}
        />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-xl text-[12px] border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] transition-colors">Skip</button>
          <button onClick={() => { if (value.trim()) onSubmit(value.trim(), type); else onSubmit(null, null); }}
            className="flex-1 px-4 py-2 rounded-xl text-[12px] font-semibold bg-[#5A90F5] hover:bg-[#4A80E5] text-white transition-colors">
            Mark complete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── StepCard ────────────────────────────────────────────────────────────────
function StepCard({ step, index, isActive, isCurrent, boardColor, sequential, onComplete, onUncomplete, onEdit, onDelete, onToggleLock, totalSteps }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showProof, setShowProof] = useState(false);
  const isCompleted = !!step.completed_at;
  const isLocked = step.locked && !isCompleted;
  const overdue = isOverdue(step.due_date) && !isCompleted;

  return (
    <>
      <div
        className="flex-shrink-0 relative rounded-2xl border transition-all duration-300 group"
        style={{
          width: 260,
          minHeight: 200,
          background: isCompleted ? "#0D0D0D" : isLocked ? "#0A0A0A" : "#111",
          borderColor: isCompleted ? "#1A1A1A" : isCurrent ? boardColor + "60" : overdue ? "#EF444440" : "#2A2A2A",
          boxShadow: isCurrent ? `0 0 0 1px ${boardColor}30, 0 8px 32px ${boardColor}18` : "none",
          opacity: isLocked ? 0.5 : 1,
        }}>
        {/* Top color stripe */}
        <div className="h-0.5 w-full rounded-t-2xl" style={{ background: isCompleted ? "#1A1A1A" : isCurrent ? boardColor : "transparent" }} />

        {/* Step number */}
        <div className="absolute -top-3 left-4">
          <span className="text-[9px] font-mono text-[#3A3A3A] bg-[#0A0A0A] px-1.5 py-0.5 rounded border border-[#1A1A1A]">
            {String(index + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
          </span>
        </div>

        <div className="p-4 pt-5 flex flex-col gap-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0">
              {/* Status icon */}
              <div className="flex-shrink-0 mt-0.5">
                {isCompleted ? (
                  <button onClick={() => onUncomplete(step.step_id)} className="text-[#00C781] hover:text-[#00A861] transition-colors">
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                ) : isLocked ? (
                  <Lock className="w-4 h-4 text-[#3A3A3A]" />
                ) : (
                  <Circle className="w-4 h-4 text-[#3A3A3A]" />
                )}
              </div>
              <h3 className={`text-[13px] font-semibold leading-snug ${isCompleted ? "line-through text-[#3A3A3A]" : "text-[#EDEDED]"}`}>
                {step.title}
              </h3>
            </div>
            <button onClick={() => setShowMenu(v => !v)}
              className="flex-shrink-0 p-1 rounded hover:bg-[#1a1a1a] text-[#3A3A3A] hover:text-[#A1A1AA] opacity-0 group-hover:opacity-100 transition-all">
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Description */}
          {step.description && (
            <p className="text-[11px] text-[#52525B] leading-relaxed line-clamp-3">{step.description}</p>
          )}

          {/* Proof badge */}
          {isCompleted && step.proof && (
            <div className="flex items-start gap-2 p-2 bg-[#00C781]/5 border border-[#00C781]/15 rounded-xl">
              <FileText className="w-3 h-3 text-[#00C781] flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-[#00C781]/80 line-clamp-2">{step.proof}</p>
            </div>
          )}

          {/* Metadata row */}
          <div className="flex flex-wrap items-center gap-2 mt-auto">
            {step.due_date && (
              <span className={`inline-flex items-center gap-1 text-[10px] ${overdue ? "text-[#EF4444]" : "text-[#52525B]"}`}>
                <Calendar className="w-3 h-3" />
                {formatDate(step.due_date)}
              </span>
            )}
            {step.tags?.map(tag => (
              <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#1A1A1A] text-[#52525B] border border-[#2A2A2A]">{tag}</span>
            ))}
          </div>

          {/* Complete button */}
          {!isCompleted && !isLocked && (
            <button
              onClick={() => setShowProof(true)}
              className="w-full mt-1 py-2 rounded-xl text-[11px] font-semibold transition-all border"
              style={{
                background: isCurrent ? boardColor + "18" : "transparent",
                borderColor: isCurrent ? boardColor + "50" : "#2A2A2A",
                color: isCurrent ? boardColor : "#52525B",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = boardColor + "25"; e.currentTarget.style.borderColor = boardColor + "80"; }}
              onMouseLeave={e => { e.currentTarget.style.background = isCurrent ? boardColor + "18" : "transparent"; e.currentTarget.style.borderColor = isCurrent ? boardColor + "50" : "#2A2A2A"; }}>
              <span className="flex items-center justify-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Complete
              </span>
            </button>
          )}

          {isCompleted && (
            <div className="text-[10px] text-[#3A3A3A] text-center">
              {new Date(step.completed_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </div>

        {/* Context menu */}
        {showMenu && (
          <div className="absolute right-0 top-8 z-50 bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl p-1 w-44 text-xs"
            onMouseLeave={() => setShowMenu(false)}>
            <button onClick={() => { onEdit(step); setShowMenu(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#1a1a1a] text-[#A1A1AA]">
              <Edit3 className="w-3.5 h-3.5" /> Edit step
            </button>
            {!isCompleted && (
              <button onClick={() => { onToggleLock(step); setShowMenu(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#1a1a1a] text-[#A1A1AA]">
                {step.locked ? <><Unlock className="w-3.5 h-3.5" /> Unlock</> : <><Lock className="w-3.5 h-3.5" /> Lock</>}
              </button>
            )}
            <div className="h-px bg-[#2A2A2A] my-1" />
            <button onClick={() => { onDelete(step.step_id); setShowMenu(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#EF4444]/10 text-[#EF4444]">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        )}
      </div>

      {showProof && (
        <ProofModal
          step={step}
          onClose={() => setShowProof(false)}
          onSubmit={(proof, proofType) => { onComplete(step.step_id, proof, proofType); setShowProof(false); }}
        />
      )}
    </>
  );
}

// ─── NewStepCard ──────────────────────────────────────────────────────────────
function NewStepCard({ boardColor, onAdd }) {
  const [active, setActive] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [locked, setLocked] = useState(false);
  const inputRef = useRef(null);

  const submit = () => {
    if (!title.trim()) return;
    onAdd({ title: title.trim(), description: desc.trim(), due_date: dueDate || null, locked });
    setTitle(""); setDesc(""); setDueDate(""); setLocked(false); setActive(false);
  };

  if (!active) {
    return (
      <button onClick={() => { setActive(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="flex-shrink-0 rounded-2xl border border-dashed border-[#2A2A2A] hover:border-[#3A3A3A] transition-all flex items-center justify-center gap-2 text-[#3A3A3A] hover:text-[#52525B]"
        style={{ width: 260, minHeight: 200 }}>
        <Plus className="w-4 h-4" />
        <span className="text-[12px] font-medium">Add step</span>
      </button>
    );
  }

  return (
    <div className="flex-shrink-0 rounded-2xl border border-[#3A3A3A] bg-[#111] p-4 flex flex-col gap-3"
      style={{ width: 260, minHeight: 200 }}>
      <div className="h-0.5 w-full rounded-t-2xl -mt-4 mb-1 rounded-2xl" style={{ background: boardColor }} />
      <input
        ref={inputRef}
        value={title}
        onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") submit(); if (e.key === "Escape") setActive(false); }}
        placeholder="Step title..."
        className="bg-transparent text-[13px] font-semibold text-[#EDEDED] placeholder-[#3A3A3A] outline-none border-b border-[#2A2A2A] pb-2"
      />
      <textarea
        value={desc}
        onChange={e => setDesc(e.target.value)}
        placeholder="Description (optional)..."
        className="bg-transparent text-[11px] text-[#A1A1AA] placeholder-[#3A3A3A] outline-none resize-none flex-1"
        rows={2}
      />
      <div className="flex items-center gap-2">
        <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
          className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg px-2 py-1 text-[10px] text-[#A1A1AA] outline-none" />
        <button onClick={() => setLocked(v => !v)} className={`p-1.5 rounded-lg border transition-colors ${locked ? "border-[#FF8A00]/40 text-[#FF8A00]" : "border-[#2A2A2A] text-[#3A3A3A]"}`}>
          {locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
        </button>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setActive(false)} className="flex-1 py-1.5 rounded-xl text-[11px] border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA]">Cancel</button>
        <button onClick={submit} disabled={!title.trim()}
          className="flex-1 py-1.5 rounded-xl text-[11px] font-semibold text-white disabled:opacity-30 transition-colors"
          style={{ background: boardColor }}>Add</button>
      </div>
    </div>
  );
}

// ─── BoardPanel ───────────────────────────────────────────────────────────────
function BoardPanel({ board, steps, boardColor, onComplete, onUncomplete, onAddStep, onEditStep, onDeleteStep, onToggleLock, onEditBoard, onArchiveBoard, onDeleteBoard }) {
  const scrollRef = useRef(null);
  const [scrollPos, setScrollPos] = useState(0);

  const completedCount = steps.filter(s => s.completed_at).length;
  const totalCount = steps.length;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const currentIdx = steps.findIndex(s => !s.completed_at && !s.locked);
  const momentum = steps[0]?.momentum ?? 0;

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 290, behavior: "smooth" });
  };

  const onScroll = () => setScrollPos(scrollRef.current?.scrollLeft ?? 0);

  return (
    <div className="flex flex-col h-full">
      {/* Board header */}
      <div className="flex items-start justify-between px-8 pt-6 pb-4 border-b border-[#1A1A1A]">
        <div className="flex items-start gap-3 min-w-0">
          {board.emoji && <span className="text-2xl">{board.emoji}</span>}
          <div className="min-w-0">
            <h2 className="text-[20px] font-bold text-[#EDEDED] leading-tight">{board.name}</h2>
            {board.description && <p className="text-[12px] text-[#52525B] mt-0.5 truncate">{board.description}</p>}
            <div className="flex items-center gap-4 mt-2">
              <span className="text-[11px] text-[#52525B]">{completedCount} / {totalCount} steps</span>
              <MomentumBadge momentum={momentum} />
              {board.sequential && (
                <span className="text-[10px] text-[#3A3A3A] flex items-center gap-1">
                  <ArrowRight className="w-3 h-3" /> Sequential
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onEditBoard(board)} className="p-2 rounded-xl hover:bg-[#1a1a1a] text-[#52525B] hover:text-[#A1A1AA] transition-colors">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onArchiveBoard(board.board_id)} className="p-2 rounded-xl hover:bg-[#1a1a1a] text-[#52525B] hover:text-[#A1A1AA] transition-colors">
            <Archive className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDeleteBoard(board.board_id)} className="p-2 rounded-xl hover:bg-[#EF4444]/10 text-[#52525B] hover:text-[#EF4444] transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-8 py-3 border-b border-[#1A1A1A]">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1 bg-[#1A1A1A] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width: `${pct}%`, background: pct === 100 ? "#00C781" : boardColor }} />
          </div>
          <span className="text-[11px] font-mono text-[#52525B] w-8 text-right">{pct}%</span>
        </div>
      </div>

      {/* Carousel */}
      <div className="flex-1 relative min-h-0 flex flex-col">
        {/* Scroll arrows */}
        {scrollPos > 20 && (
          <button onClick={() => scroll(-1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#111] border border-[#2A2A2A] flex items-center justify-center text-[#A1A1AA] hover:text-white hover:bg-[#1a1a1a] shadow-xl transition-all">
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => scroll(1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#111] border border-[#2A2A2A] flex items-center justify-center text-[#A1A1AA] hover:text-white hover:bg-[#1a1a1a] shadow-xl transition-all">
          <ChevronRight className="w-4 h-4" />
        </button>

        <div ref={scrollRef} onScroll={onScroll}
          className="flex-1 overflow-x-auto overflow-y-hidden px-8 py-6 flex items-center gap-4"
          style={{ scrollbarWidth: "none" }}>
          {steps.map((step, i) => (
            <StepCard
              key={step.step_id}
              step={step}
              index={i}
              totalSteps={totalCount}
              isActive={!step.completed_at && !step.locked}
              isCurrent={i === currentIdx}
              boardColor={boardColor}
              sequential={board.sequential}
              onComplete={(id, proof, proofType) => onComplete(board.board_id, id, proof, proofType)}
              onUncomplete={(id) => onUncomplete(board.board_id, id)}
              onEdit={onEditStep}
              onDelete={(id) => onDeleteStep(board.board_id, id)}
              onToggleLock={(s) => onToggleLock(board.board_id, s)}
            />
          ))}
          <NewStepCard boardColor={boardColor} onAdd={(data) => onAddStep(board.board_id, data)} />

          {/* End cap */}
          {pct === 100 && totalCount > 0 && (
            <div className="flex-shrink-0 flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#00C781]/20 bg-[#00C781]/5 px-8"
              style={{ width: 200, minHeight: 200 }}>
              <CheckCircle2 className="w-8 h-8 text-[#00C781]" />
              <p className="text-[12px] font-semibold text-[#00C781] text-center">Board complete</p>
              <p className="text-[10px] text-[#52525B] text-center">{completedCount} steps finished</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── BoardFormModal ───────────────────────────────────────────────────────────
function BoardFormModal({ initial, onClose, onSave }) {
  const [name, setName] = useState(initial?.name || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [color, setColor] = useState(initial?.color || "#5A90F5");
  const [emoji, setEmoji] = useState(initial?.emoji || "");
  const [sequential, setSequential] = useState(initial?.sequential ?? true);

  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-sm shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-[#EDEDED] mb-4">{initial ? "Edit board" : "New board"}</h3>
        <div className="flex gap-2 mb-3">
          <div className="w-10 h-10 rounded-xl border border-[#2A2A2A] flex items-center justify-center text-xl cursor-pointer hover:bg-[#1a1a1a]"
            style={{ background: color + "20" }}
            title="Choose emoji">
            {emoji || "📋"}
          </div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Board name..."
            className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[13px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5]" />
        </div>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)..."
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[12px] text-[#A1A1AA] placeholder-[#3A3A3A] outline-none resize-none mb-3 focus:border-[#5A90F5]"
          rows={2} />
        {/* Emoji picker */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {EMOJIS.map(e => (
            <button key={e} onClick={() => setEmoji(e)}
              className={`text-lg w-8 h-8 rounded-lg flex items-center justify-center transition-all ${emoji === e ? "bg-[#2A2A2A]" : "hover:bg-[#1a1a1a]"}`}>
              {e}
            </button>
          ))}
        </div>
        {/* Color picker */}
        <div className="flex gap-1.5 mb-4">
          {COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)}
              className="w-6 h-6 rounded-full border-2 transition-all"
              style={{ background: c, borderColor: color === c ? "#fff" : "transparent" }} />
          ))}
        </div>
        {/* Sequential toggle */}
        <label className="flex items-center gap-3 mb-4 cursor-pointer">
          <div onClick={() => setSequential(v => !v)}
            className={`w-9 h-5 rounded-full border transition-colors ${sequential ? "border-transparent" : "border-[#2A2A2A] bg-[#0A0A0A]"}`}
            style={{ background: sequential ? color : undefined }}>
            <div className={`w-3 h-3 rounded-full bg-white shadow mt-0.5 mx-0.5 transition-all ${sequential ? "translate-x-4" : "translate-x-0"}`} />
          </div>
          <div>
            <p className="text-[12px] text-[#EDEDED]">Sequential steps</p>
            <p className="text-[10px] text-[#52525B]">Each step unlocks after previous is done</p>
          </div>
        </label>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-xl text-[12px] border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA]">Cancel</button>
          <button onClick={() => { if (name.trim()) onSave({ name: name.trim(), description: desc, color, emoji, sequential }); }}
            disabled={!name.trim()}
            className="flex-1 py-2 rounded-xl text-[12px] font-semibold text-white disabled:opacity-30 transition-colors"
            style={{ background: color }}>
            {initial ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ProgressBoards ──────────────────────────────────────────────────────
export default function ProgressBoards() {
  const { current } = useWorkspace();
  const [boards, setBoards] = useState([]);
  const [steps, setSteps] = useState({}); // { board_id: [Step] }
  const [activeBoardId, setActiveBoardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewBoard, setShowNewBoard] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [contextResume, setContextResume] = useState(null);
  const [showResume, setShowResume] = useState(false);

  const wsId = current?.workspace_id;

  const checkContextResume = useCallback(async (boardId) => {
    if (!wsId || !boardId) return;
    const key = `crescendo:board-visited:${boardId}`;
    const last = localStorage.getItem(key);
    const now = Date.now();
    localStorage.setItem(key, String(now));
    // Show resume if away for more than 2 days
    if (!last || now - Number(last) < 2 * 24 * 60 * 60 * 1000) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/boards/${boardId}/context`);
      setContextResume(data);
      setShowResume(true);
    } catch {}
  }, [wsId]);

  const loadBoards = useCallback(async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/boards`, { params: { archived: showArchived } });
      setBoards(data);
      if (!activeBoardId && data.length > 0) setActiveBoardId(data[0].board_id);
    } catch { toast.error("Failed to load boards"); }
    finally { setLoading(false); }
  }, [wsId, showArchived, activeBoardId]);

  const loadSteps = useCallback(async (boardId) => {
    if (!wsId || !boardId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/boards/${boardId}/steps`);
      setSteps(s => ({ ...s, [boardId]: data }));
    } catch {}
  }, [wsId]);

  useEffect(() => { loadBoards(); }, [wsId, showArchived]);
  useEffect(() => {
    if (activeBoardId) {
      loadSteps(activeBoardId);
      checkContextResume(activeBoardId);
    }
  }, [activeBoardId]);

  const createBoard = async (data) => {
    try {
      const { data: board } = await api.post(`/workspaces/${wsId}/boards`, data);
      setBoards(b => [board, ...b]);
      setActiveBoardId(board.board_id);
      setShowNewBoard(false);
      toast.success("Board created");
    } catch { toast.error("Failed to create board"); }
  };

  const updateBoard = async (data) => {
    try {
      const { data: board } = await api.put(`/workspaces/${wsId}/boards/${editingBoard.board_id}`, data);
      setBoards(b => b.map(x => x.board_id === board.board_id ? board : x));
      setEditingBoard(null);
      toast.success("Board updated");
    } catch { toast.error("Failed to update board"); }
  };

  const archiveBoard = async (boardId) => {
    try {
      await api.put(`/workspaces/${wsId}/boards/${boardId}`, { archived: true });
      setBoards(b => b.filter(x => x.board_id !== boardId));
      if (activeBoardId === boardId) setActiveBoardId(boards.find(b => b.board_id !== boardId)?.board_id || null);
      toast.success("Board archived");
    } catch {}
  };

  const deleteBoard = async (boardId) => {
    if (!window.confirm("Delete this board and all its steps?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/boards/${boardId}`);
      setBoards(b => b.filter(x => x.board_id !== boardId));
      setSteps(s => { const n = { ...s }; delete n[boardId]; return n; });
      if (activeBoardId === boardId) setActiveBoardId(boards.find(b => b.board_id !== boardId)?.board_id || null);
      toast.success("Board deleted");
    } catch {}
  };

  const addStep = async (boardId, data) => {
    try {
      const { data: step } = await api.post(`/workspaces/${wsId}/boards/${boardId}/steps`, data);
      setSteps(s => ({ ...s, [boardId]: [...(s[boardId] || []), step] }));
      setBoards(b => b.map(x => x.board_id === boardId ? { ...x, step_count: (x.step_count || 0) + 1 } : x));
    } catch { toast.error("Failed to add step"); }
  };

  const completeStep = async (boardId, stepId, proof, proofType) => {
    try {
      await api.post(`/workspaces/${wsId}/boards/${boardId}/steps/${stepId}/complete`, { proof, proof_type: proofType });
      await loadSteps(boardId);
      setBoards(b => b.map(x => x.board_id === boardId ? { ...x, completed_count: (x.completed_count || 0) + 1 } : x));
      toast.success("Step completed");
    } catch { toast.error("Failed to complete step"); }
  };

  const uncompleteStep = async (boardId, stepId) => {
    try {
      await api.post(`/workspaces/${wsId}/boards/${boardId}/steps/${stepId}/uncomplete`);
      await loadSteps(boardId);
    } catch {}
  };

  const deleteStep = async (boardId, stepId) => {
    try {
      await api.delete(`/workspaces/${wsId}/boards/${boardId}/steps/${stepId}`);
      setSteps(s => ({ ...s, [boardId]: (s[boardId] || []).filter(x => x.step_id !== stepId) }));
      setBoards(b => b.map(x => x.board_id === boardId ? { ...x, step_count: Math.max(0, (x.step_count || 0) - 1) } : x));
    } catch {}
  };

  const toggleLock = async (boardId, step) => {
    try {
      await api.put(`/workspaces/${wsId}/boards/${boardId}/steps/${step.step_id}`, { locked: !step.locked });
      setSteps(s => ({ ...s, [boardId]: (s[boardId] || []).map(x => x.step_id === step.step_id ? { ...x, locked: !x.locked } : x) }));
    } catch {}
  };

  const activeBoard = boards.find(b => b.board_id === activeBoardId);
  const activeSteps = steps[activeBoardId] || [];

  return (
    <div className="flex h-full bg-[#0A0A0A] overflow-hidden">
      {/* Sidebar */}
      <div className="w-56 flex-shrink-0 border-r border-[#1A1A1A] flex flex-col bg-[#090909]">
        <div className="px-4 py-4 border-b border-[#1A1A1A] flex items-center justify-between">
          <h2 className="text-[12px] font-semibold text-[#52525B] uppercase tracking-wider">Boards</h2>
          <button onClick={() => setShowNewBoard(true)}
            className="w-5 h-5 rounded-md bg-[#1a1a1a] hover:bg-[#2A2A2A] flex items-center justify-center text-[#52525B] hover:text-[#EDEDED] transition-colors">
            <Plus className="w-3 h-3" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {loading ? (
            <div className="px-4 py-3 text-[11px] text-[#3A3A3A]">Loading…</div>
          ) : boards.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-[11px] text-[#3A3A3A] mb-3">No boards yet</p>
              <button onClick={() => setShowNewBoard(true)}
                className="text-[11px] text-[#5A90F5] hover:text-[#4A80E5]">Create first board →</button>
            </div>
          ) : (
            boards.map(board => {
              const pct = board.step_count > 0 ? Math.round((board.completed_count / board.step_count) * 100) : 0;
              const isActive = board.board_id === activeBoardId;
              return (
                <button key={board.board_id} onClick={() => setActiveBoardId(board.board_id)}
                  className={`w-full text-left px-3 py-2.5 mx-1 rounded-xl transition-all ${isActive ? "bg-[#141414]" : "hover:bg-[#0F0F0F]"}`}
                  style={{ width: "calc(100% - 8px)" }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: board.color }} />
                    <span className="text-[11px] font-medium truncate" style={{ color: isActive ? "#EDEDED" : "#A1A1AA" }}>
                      {board.emoji && <span className="mr-1">{board.emoji}</span>}{board.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pl-4">
                    <div className="flex-1 h-0.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: board.color }} />
                    </div>
                    <span className="text-[9px] text-[#3A3A3A] font-mono">{pct}%</span>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Archive toggle */}
        <div className="px-4 py-3 border-t border-[#1A1A1A]">
          <button onClick={() => setShowArchived(v => !v)}
            className="w-full text-[10px] text-[#3A3A3A] hover:text-[#52525B] flex items-center gap-1.5 transition-colors">
            <Archive className="w-3 h-3" />
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 min-w-0 flex flex-col">
        {!activeBoard ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center">
              <Flag className="w-7 h-7 text-[#3A3A3A]" />
            </div>
            <div className="text-center">
              <p className="text-[15px] font-semibold text-[#3A3A3A]">No board selected</p>
              <p className="text-[12px] text-[#2A2A2A] mt-1">Create a board to start building your linear flow</p>
            </div>
            <button onClick={() => setShowNewBoard(true)}
              className="px-5 py-2.5 rounded-xl text-[12px] font-semibold bg-[#5A90F5] hover:bg-[#4A80E5] text-white transition-colors">
              Create first board
            </button>
          </div>
        ) : (
          <BoardPanel
            board={activeBoard}
            steps={activeSteps}
            boardColor={activeBoard.color || "#5A90F5"}
            onComplete={completeStep}
            onUncomplete={uncompleteStep}
            onAddStep={addStep}
            onEditStep={() => {}}
            onDeleteStep={deleteStep}
            onToggleLock={toggleLock}
            onEditBoard={(b) => setEditingBoard(b)}
            onArchiveBoard={archiveBoard}
            onDeleteBoard={deleteBoard}
          />
        )}
      </div>

      {/* Modals */}
      {showNewBoard && <BoardFormModal onClose={() => setShowNewBoard(false)} onSave={createBoard} />}
      {editingBoard && <BoardFormModal initial={editingBoard} onClose={() => setEditingBoard(null)} onSave={updateBoard} />}

      {/* Context Resume */}
      {showResume && contextResume && (
        <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={() => setShowResume(false)}>
          <div className="bg-[#141414] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Welcome back</p>
                <h3 className="text-[16px] font-bold text-[#EDEDED]">{contextResume.board_name}</h3>
              </div>
              <button onClick={() => setShowResume(false)} className="p-1.5 rounded-lg hover:bg-[#1A1A1A] text-[#71717A]">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Progress */}
              <div className="p-3 bg-[#1A1A1A] rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-[#A1A1AA]">Progress</span>
                  <span className="text-[11px] font-semibold text-[#EDEDED]">{contextResume.pct_complete}%</span>
                </div>
                <div className="h-1.5 bg-[#2A2A2A] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#5A90F5] transition-all" style={{ width: `${contextResume.pct_complete}%` }} />
                </div>
                <p className="text-[10px] text-[#71717A] mt-1.5">{contextResume.completed_count} of {contextResume.total_steps} steps done</p>
              </div>

              {/* Last action */}
              {contextResume.last_completed_step && (
                <div className="p-3 bg-[#1A1A1A] rounded-xl">
                  <p className="text-[10px] text-[#71717A] mb-1">Last completed</p>
                  <p className="text-[12px] text-[#00C781]">✓ {contextResume.last_completed_step}</p>
                  {contextResume.last_completed_at && (
                    <p className="text-[10px] text-[#71717A] mt-0.5">{new Date(contextResume.last_completed_at).toLocaleDateString([], { month: "short", day: "numeric" })}</p>
                  )}
                </div>
              )}

              {/* Current step */}
              {contextResume.current_step && (
                <div className="p-3 bg-[#5A90F5]/10 border border-[#5A90F5]/25 rounded-xl">
                  <p className="text-[10px] text-[#5A90F5] mb-1">Up next</p>
                  <p className="text-[12px] font-semibold text-[#EDEDED]">{contextResume.current_step}</p>
                  {contextResume.current_step_due && (
                    <p className="text-[10px] text-[#71717A] mt-0.5">Due {new Date(contextResume.current_step_due).toLocaleDateString([], { month: "short", day: "numeric" })}</p>
                  )}
                </div>
              )}

              {/* Tensions */}
              {contextResume.tensions?.length > 0 && (
                <div className="p-3 bg-[#EF4444]/8 border border-[#EF4444]/20 rounded-xl">
                  <p className="text-[10px] text-[#EF4444] mb-1.5">Issues since you were last here</p>
                  {contextResume.tensions.map((t, i) => (
                    <p key={i} className="text-[11px] text-[#EDEDED]">• {t}</p>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => setShowResume(false)} className="w-full mt-4 py-2.5 rounded-xl text-[12px] font-semibold bg-[#5A90F5] hover:bg-[#4A80E5] text-white transition-colors">
              Continue where I left off
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
